import { TagIcon } from "@heroicons/react/24/outline";
import { Button, Card, Separator, toast } from "@mindstonehq/ui";
import copy from "copy-to-clipboard";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useUser } from "~/hooks/context-hook";
import { usePlatformAccess } from "~/hooks/platform-access";
import metrics from "~/utils/metrics";
import { CopyIcon } from "../icons";
import LogAIUsageDialog from "./log-ai-usage-dialog";
import UserUsageChart from "./user-usage-chart";

// Simple error boundary component for the chart section
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Chart error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 rounded-md border border-border flex flex-col items-center gap-3 text-center">
          <div className="text-sm text-muted-foreground">
            Unable to load chart. Please try refreshing the page.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function ProfileOnlyYou({
  profile,
}: {
  profile: {
    id: string;
  };
}) {
  const me = useUser();
  const [searchParams] = useSearchParams();
  const isCurrentUser = profile.id === me?.id;
  const [showLogAIDialog, setShowLogAIDialog] = useState(false);
  const referralCode = me?.referrals?.[0]?.referralCode;
  // Entitlement = active Plus billing OR the self-serve policy (see
  // ~/hooks/platform-access). Keying on `billing.status === ACTIVE` alone hid
  // the AI-growth chart and the "Log AI growth" button from self-serve
  // learners, who have no billing row at all by design. Strictly ADDITIVE —
  // a lapsed-Plus Live learner is still not entitled and still sees neither.
  const hasPlatformAccess = usePlatformAccess();
  const showAIUsage = isCurrentUser && hasPlatformAccess;
  const showDiscountCode = isCurrentUser && Boolean(referralCode);

  useEffect(() => {
    if (searchParams.has("tab") && searchParams.get("tab") === "ai-usage") {
      setShowLogAIDialog(true);
    }
  }, [searchParams]);

  const handleLogAIUsageClick = () => {
    metrics.track("open_logai_dialog", {
      source: "profile_chart_section",
    });
    setShowLogAIDialog(true);
  };

  const handleCopy = () => {
    if (referralCode) {
      copy(referralCode);
      toast.success("Discount code copied to clipboard");
    }
  };

  if (!showAIUsage && !showDiscountCode) return null;

  return (
    <Card className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-1">
        <div className="text-base font-semibold leading-tight">
          Only you can see this
        </div>
        <div className="text-sm text-muted-foreground">
          Private tools for your learning and referrals. They are not shown on
          your public profile.
        </div>
      </div>

      {showAIUsage && (
        <>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between">
              <div className="flex-1 min-w-60">
                <h2 className="text-base font-semibold text-foreground mb-1">
                  Your personal AI growth
                </h2>
              </div>
              <Button onClick={handleLogAIUsageClick} size="default">
                Log AI growth
              </Button>
            </div>

            {/* Chart container with responsive layout */}
            <div className="w-full flex-1">
              <ChartErrorBoundary>
                <UserUsageChart />
              </ChartErrorBoundary>
            </div>
          </div>
          {showDiscountCode && <Separator />}

          {/* LogAI Usage Dialog */}
          <LogAIUsageDialog
            open={showLogAIDialog}
            onOpenChange={setShowLogAIDialog}
          />
        </>
      )}

      {showDiscountCode && (
        <Card className="flex flex-col gap-3 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold leading-tight">
              Your discount code
            </div>
            <TagIcon className="w-5 h-5" />
          </div>
          <div className="text-sm text-muted-foreground">
            Use your 20% discount code to join another Mindstone program or
            share it with a friend or colleague interested in AI.
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 border border-dashed border-border rounded-md px-3 py-1.5">
              <code className="text-sm font-medium text-placeholder-foreground">
                {referralCode}
              </code>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              aria-label="Copy discount code"
              className="text-placeholder-foreground hover:text-foreground border border-border"
            >
              <CopyIcon className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}
    </Card>
  );
}
